import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, where, writeBatch } from 'firebase/firestore';
import { ArrowLeft, CreditCard as Edit2, Trash2, Save, X, Merge, Loader2, CheckCircle, Download, Search, Zap, AlertTriangle, Flag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Registration } from '../types';

interface LicensePlateGroup {
  id: string;
  licensePlate: string;
  count: number;
  registrations: Registration[];
}

interface SimilarGroup {
  canonical: string;
  variants: string[];
  count: number;
  registrations: Registration[];
}

interface MergeOperation {
  from: string;
  to: string;
  count: number;
}

interface CountryGroup {
  id: string;
  countryCode: string;
  count: number;
  registrations: Registration[];
}

export default function ManageLicensePlates() {
  const { signOut } = useAuth();
  const [licensePlates, setLicensePlates] = useState<LicensePlateGroup[]>([]);
  const [filteredPlates, setFilteredPlates] = useState<LicensePlateGroup[]>([]);
  const [similarGroups, setSimilarGroups] = useState<SimilarGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [selectedPlates, setSelectedPlates] = useState<Set<string>>(new Set());
  const [canonicalName, setCanonicalName] = useState('');
  const [merging, setMerging] = useState(false);
  const [mergeProgress, setMergeProgress] = useState<{ current: number; total: number } | null>(null);
  const [mergeResults, setMergeResults] = useState<MergeOperation[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSimilarGroups, setShowSimilarGroups] = useState(false);
  const [showCountries, setShowCountries] = useState(false);
  const [countries, setCountries] = useState<CountryGroup[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<CountryGroup[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<Set<string>>(new Set());
  const [canonicalCountry, setCanonicalCountry] = useState('');
  const [countrySearchTerm, setCountrySearchTerm] = useState('');

  useEffect(() => {
    fetchLicensePlates();
  }, []);

  // Filter plates based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredPlates(licensePlates);
    } else {
      const filtered = licensePlates.filter(plate =>
        plate.licensePlate.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPlates(filtered);
    }
  }, [searchTerm, licensePlates]);

  // Filter countries based on search term
  useEffect(() => {
    if (!countrySearchTerm.trim()) {
      setFilteredCountries(countries);
    } else {
      const filtered = countries.filter(country =>
        country.countryCode.toLowerCase().includes(countrySearchTerm.toLowerCase())
      );
      setFilteredCountries(filtered);
    }
  }, [countrySearchTerm, countries]);

  const fetchLicensePlates = async () => {
    try {
      const registrationsSnapshot = await getDocs(collection(db, 'registrations'));
      const registrations = registrationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as (Registration & { id: string })[];
      
      // Group registrations by license plate
      const plateMap = new Map<string, Registration[]>();
      registrations.forEach(reg => {
        const plate = reg.licensePlate;
        if (!plateMap.has(plate)) {
          plateMap.set(plate, []);
        }
        plateMap.get(plate)!.push(reg);
      });

      // Convert to array and sort by count (most registrations first)
      const plateGroups: LicensePlateGroup[] = Array.from(plateMap.entries()).map(([plate, regs]) => ({
        id: plate, // Use license plate as ID
        licensePlate: plate,
        count: regs.length,
        registrations: regs
      }));

      plateGroups.sort((a, b) => b.count - a.count);
      setLicensePlates(plateGroups);
      setFilteredPlates(plateGroups);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching license plates:', error);
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const registrationsSnapshot = await getDocs(collection(db, 'registrations'));
      const registrations = registrationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as (Registration & { id: string })[];
      
      // Group registrations by country code
      const countryMap = new Map<string, Registration[]>();
      registrations.forEach(reg => {
        const country = reg.countryCode || 'Unknown';
        if (!countryMap.has(country)) {
          countryMap.set(country, []);
        }
        countryMap.get(country)!.push(reg);
      });

      // Convert to array and sort by count (most registrations first)
      const countryGroups: CountryGroup[] = Array.from(countryMap.entries()).map(([country, regs]) => ({
        id: country, // Use country code as ID
        countryCode: country,
        count: regs.length,
        registrations: regs
      }));

      countryGroups.sort((a, b) => b.count - a.count);
      setCountries(countryGroups);
      setFilteredCountries(countryGroups);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  // Normalize license plate for comparison
  const normalizeLicensePlate = (plate: string): string => {
    return plate
      .trim()
      .toLowerCase()
      .replace(/[\s\-]/g, '') // Remove spaces and hyphens
      .replace(/[^a-z0-9]/g, ''); // Remove any other special characters
  };

  // Find similar license plates
  const findSimilarPlates = (): SimilarGroup[] => {
    const groups = new Map<string, string[]>();
    
    // Group plates by normalized version
    licensePlates.forEach(plate => {
      const normalized = normalizeLicensePlate(plate.licensePlate);
      if (!groups.has(normalized)) {
        groups.set(normalized, []);
      }
      groups.get(normalized)!.push(plate.licensePlate);
    });

    // Only return groups with multiple variants (2 or more different license plates)
    return Array.from(groups.entries())
      .filter(([, variants]) => variants.length > 1)
      .map(([normalized, variants]) => {
        // Choose the most common variant as canonical
        const variantCounts = variants.map(variant => ({
          variant,
          count: licensePlates.find(p => p.licensePlate === variant)?.count || 0
        }));
        
        // Sort by count (descending), then by length (ascending for cleaner format)
        variantCounts.sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return a.variant.length - b.variant.length;
        });

        const canonical = variantCounts[0].variant;
        const otherVariants = variants.filter(v => v !== canonical);
        
        // Calculate total registrations for this group
        const allRegistrations = variants.flatMap(variant => 
          licensePlates.find(p => p.licensePlate === variant)?.registrations || []
        );
        
        return {
          canonical,
          variants: otherVariants,
          count: allRegistrations.length,
          registrations: allRegistrations
        };
      })
      .filter(group => group.variants.length > 0) // Ensure we only show groups that actually have variants to merge
      .sort((a, b) => b.count - a.count); // Sort by total registrations
  };

  const analyzeForSimilarPlates = () => {
    setAnalyzing(true);
    try {
      const groups = findSimilarPlates();
      setSimilarGroups(groups);
      setShowSimilarGroups(true);
      
      // Pre-select merges for similar groups
      const initialMerges = new Map<string, string>();
      groups.forEach(group => {
        group.variants.forEach(variant => {
          initialMerges.set(variant, group.canonical);
        });
      });
      setSelectedPlates(new Set(initialMerges.keys()));
      
      // Set first canonical as default if any groups found
      if (groups.length > 0) {
        setCanonicalName(groups[0].canonical);
      }
    } catch (error) {
      console.error('Error analyzing similar plates:', error);
      alert('Feil ved analyse av registreringsnummer');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleEdit = (plate: LicensePlateGroup) => {
    setEditingId(plate.id);
    setEditingName(plate.licensePlate);
  };

  const handleSave = async (oldPlate: string) => {
    if (!editingName.trim()) return;
    
    const newPlate = editingName.trim();
    if (oldPlate === newPlate) {
      setEditingId(null);
      setEditingName('');
      return;
    }

    try {
      // Update all registrations with the old license plate
      const registrationsSnapshot = await getDocs(
        query(collection(db, 'registrations'), where('licensePlate', '==', oldPlate))
      );

      const batch = writeBatch(db);
      registrationsSnapshot.docs.forEach(docSnapshot => {
        batch.update(docSnapshot.ref, { licensePlate: newPlate });
      });

      await batch.commit();
      
      // Refresh the data
      await fetchLicensePlates();
      
      setEditingId(null);
      setEditingName('');
    } catch (error) {
      console.error('Error updating license plate:', error);
      alert('Feil ved oppdatering av registreringsnummer');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleDeleteAll = async (licensePlate: string, count: number) => {
    if (!window.confirm(`Er du sikker på at du vil slette ALLE ${count} registreringer for kjøretøy "${licensePlate}"? Dette kan ikke angres.`)) {
      return;
    }

    try {
      // Find all registrations with this license plate
      const registrationsSnapshot = await getDocs(
        query(collection(db, 'registrations'), where('licensePlate', '==', licensePlate))
      );

      // Delete all registrations in batches
      const batch = writeBatch(db);
      registrationsSnapshot.docs.forEach(docSnapshot => {
        batch.delete(docSnapshot.ref);
      });

      await batch.commit();
      
      // Refresh the license plates list
      await fetchLicensePlates();
      
      alert(`${count} registreringer for "${licensePlate}" ble slettet.`);
    } catch (error) {
      console.error('Error deleting all registrations:', error);
      alert('Feil ved sletting av registreringer');
    }
  };

  const handlePlateSelection = (plateName: string, checked: boolean) => {
    const newSelected = new Set(selectedPlates);
    if (checked) {
      newSelected.add(plateName);
    } else {
      newSelected.delete(plateName);
    }
    setSelectedPlates(newSelected);
    
    // Reset canonical name if it's no longer in selection
    if (!newSelected.has(canonicalName)) {
      setCanonicalName(newSelected.size > 0 ? Array.from(newSelected)[0] : '');
    }
  };

  const handleCanonicalChange = (groupIndex: number, newCanonical: string) => {
    const updatedGroups = [...similarGroups];
    const group = updatedGroups[groupIndex];
    
    // Remove old canonical from variants and add to variants
    if (group.canonical !== newCanonical) {
      group.variants.push(group.canonical);
      group.variants = group.variants.filter(v => v !== newCanonical);
      group.canonical = newCanonical;
    }
    
    setSimilarGroups(updatedGroups);
    
    // Update selected plates
    const newSelected = new Set(selectedPlates);
    group.variants.forEach(variant => {
      newSelected.add(variant);
    });
    newSelected.delete(newCanonical); // Remove canonical from selection
    setSelectedPlates(newSelected);
    setCanonicalName(newCanonical);
  };

  const executeMergeForGroup = async (groupIndex: number) => {
    const group = similarGroups[groupIndex];
    const groupMerges = group.variants.map(variant => [variant, group.canonical] as [string, string]);
    
    if (groupMerges.length === 0) {
      alert('Ingen sammenslåinger i denne gruppen');
      return;
    }

    if (!confirm(`Er du sikker på at du vil slå sammen ${groupMerges.length} registreringsnummer i denne gruppen? Dette kan ikke angres.`)) {
      return;
    }

    await executeMergeForGroups(groupMerges);
    
    // Remove the processed group from the list
    const updatedGroups = similarGroups.filter((_, index) => index !== groupIndex);
    setSimilarGroups(updatedGroups);
    
    // Update selected plates to remove processed items
    const newSelected = new Set(selectedPlates);
    group.variants.forEach(variant => {
      newSelected.delete(variant);
    });
    setSelectedPlates(newSelected);
  };

  const executeMerge = async () => {
    if (selectedPlates.size < 1) {
      alert('Velg minst 1 registreringsnummer for å slå sammen');
      return;
    }

    if (!canonicalName) {
      alert('Velg et kanonisk registreringsnummer');
      return;
    }

    const platesToMerge = Array.from(selectedPlates).filter(name => name !== canonicalName);
    
    if (platesToMerge.length === 0) {
      alert('Ingen registreringsnummer å slå sammen');
      return;
    }

    if (!confirm(`Er du sikker på at du vil slå sammen ${platesToMerge.length} registreringsnummer til "${canonicalName}"? Dette kan ikke angres.`)) {
      return;
    }

    await executeMergeForGroups(platesToMerge.map(plate => [plate, canonicalName] as [string, string]));
  };

  const executeMergeAllGroups = async () => {
    if (similarGroups.length === 0) {
      alert('Ingen grupper å slå sammen');
      return;
    }

    const totalVariants = similarGroups.reduce((sum, group) => sum + group.variants.length, 0);
    
    if (!confirm(`Er du sikker på at du vil slå sammen alle ${similarGroups.length} grupper (totalt ${totalVariants} registreringsnummer)? Dette kan ikke angres.`)) {
      return;
    }

    // Create merge entries for all groups
    const allMergeEntries: [string, string][] = [];
    similarGroups.forEach(group => {
      group.variants.forEach(variant => {
        allMergeEntries.push([variant, group.canonical]);
      });
    });

    await executeMergeForGroups(allMergeEntries);
    
    // Clear all groups after successful merge
    setSimilarGroups([]);
    setShowSimilarGroups(false);
    setSelectedPlates(new Set());
    setCanonicalName('');
  };
  const executeMergeForGroups = async (mergeEntries: [string, string][]) => {
    setMerging(true);
    setMergeProgress({ current: 0, total: mergeEntries.length });
    const results: MergeOperation[] = [];

    try {
      let processed = 0;
      
      // Process merges in batches
      const batchSize = 10;
      
      for (let i = 0; i < mergeEntries.length; i += batchSize) {
        const batch = writeBatch(db);
        const batchPlates = mergeEntries.slice(i, i + batchSize);
        
        for (const [fromPlate, toPlate] of batchPlates) {
          // Find all registrations with the old license plate
          const registrationsSnapshot = await getDocs(
            query(collection(db, 'registrations'), where('licensePlate', '==', fromPlate))
          );
          
          const count = registrationsSnapshot.docs.length;
          if (count > 0) {
            // Update all registrations
            registrationsSnapshot.docs.forEach(docSnapshot => {
              batch.update(docSnapshot.ref, { licensePlate: toPlate });
            });
            
            results.push({ from: fromPlate, to: toPlate, count });
          }
          
          processed++;
          setMergeProgress({ current: processed, total: mergeEntries.length });
        }
        
        // Commit the batch
        await batch.commit();
      }

      setMergeResults(results);
      
      // Only show results if this was a full merge (not individual group)
      if (mergeEntries.length === selectedPlates.size || mergeEntries.length > 1) {
        setShowResults(true);
        setSimilarGroups([]);
        setShowSimilarGroups(false);
      }
      
      // Refresh the license plates list
      await fetchLicensePlates();
      
      // Reset selection state
      setSelectedPlates(new Set());
      setCanonicalName('');
      
    } catch (error) {
      console.error('Error executing merge:', error);
      alert('Feil ved sammenslåing av data');
    } finally {
      setMerging(false);
      setMergeProgress(null);
    }
  };

  const downloadReport = () => {
    const csvContent = [
      'Fra,Til,Antall registreringer',
      ...mergeResults.map(result => `${result.from},${result.to},${result.count}`)
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `registreringsnummer_sammenslåing_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const resetResults = () => {
    setShowResults(false);
    setMergeResults([]);
  };

  const resetAnalysis = () => {
    setSimilarGroups([]);
    setShowSimilarGroups(false);
    setSelectedPlates(new Set());
    setCanonicalName('');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <Link
                to="/"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors duration-150"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Tilbake til dashboard
              </Link>
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Administrer registreringsnummer</h1>
            <button
              onClick={() => signOut()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Logg ut
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {showResults && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <CheckCircle className="h-8 w-8 text-green-500 mr-3" />
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Sammenslåing fullført</h2>
                    <p className="text-gray-600">
                      {mergeResults.length} registreringsnummer ble slått sammen, 
                      totalt {mergeResults.reduce((sum, r) => sum + r.count, 0)} registreringer oppdatert
                    </p>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={downloadReport}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Last ned rapport
                  </button>
                  <button
                    onClick={resetResults}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Lukk
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fra
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Til
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Registreringer oppdatert
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {mergeResults.map((result, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {result.from}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {result.to}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {result.count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Auto-detection section */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Automatisk oppdaging av lignende registreringsnummer</h2>
            <p className="text-gray-600 mb-6">
              Dette verktøyet finner automatisk registreringsnummer som kan være det samme kjøretøyet med forskjellig formatering 
              (f.eks. "ABC123", "ABC 123", "ABC-123").
            </p>
            
            <div className="flex space-x-4">
              <button
                onClick={analyzeForSimilarPlates}
                disabled={analyzing || loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyserer...
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 mr-2" />
                    Finn lignende registreringsnummer
                  </>
                )}
              </button>
              
              {showSimilarGroups && (
                <button
                  onClick={resetAnalysis}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Tilbake til liste
                </button>
              )}
            </div>
          </div>

          {/* Similar groups section */}
          {showSimilarGroups && similarGroups.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <AlertTriangle className="h-6 w-6 text-yellow-500 mr-2" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Funnet {similarGroups.length} grupper med lignende registreringsnummer
                    </h3>
                    <p className="text-sm text-gray-600">
                      Velg det kanoniske registreringsnummeret for hver gruppe. Alle varianter vil bli slått sammen til dette nummeret.
                    </p>
                  </div>
                </div>
                {selectedPlates.size > 0 && canonicalName && (
                  <button
                    onClick={executeMerge}
                    disabled={merging}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
                  >
                    {merging ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Slår sammen...
                      </>
                    ) : (
                      <>
                        <Merge className="h-4 w-4 mr-2" />
                        Slå sammen alle ({selectedPlates.size})
                      </>
                    )}
                  </button>
                )}
                {similarGroups.length > 0 && (
                  <button
                    onClick={executeMergeAllGroups}
                    disabled={merging}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
                  >
                    {merging ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Slår sammen...
                      </>
                    ) : (
                      <>
                        <Merge className="h-4 w-4 mr-2" />
                        Slå sammen alle grupper ({similarGroups.length})
                      </>
                    )}
                  </button>
                )}
              </div>

              <div className="space-y-6">
                {similarGroups.map((group, groupIndex) => (
                  <div key={groupIndex} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900">
                          Gruppe {groupIndex + 1}
                        </span>
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {group.count} registreringer
                        </span>
                      </div>
                      <button
                        onClick={() => executeMergeForGroup(groupIndex)}
                        disabled={merging}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400"
                      >
                        {merging ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Behandler...
                          </>
                        ) : (
                          <>
                            <Merge className="h-4 w-4 mr-2" />
                            Slå sammen denne gruppen
                          </>
                        )}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Kanonisk registreringsnummer (alle vil bli endret til dette):
                        </label>
                        <select
                          value={group.canonical}
                          onChange={(e) => handleCanonicalChange(groupIndex, e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value={group.canonical}>{group.canonical}</option>
                          {group.variants.map(variant => (
                            <option key={variant} value={variant}>{variant}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Varianter som vil bli slått sammen:
                        </label>
                        <div className="space-y-1">
                          {group.variants.map(variant => (
                            <div key={variant} className="flex items-center text-sm">
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                                "{variant}"
                              </span>
                              <span className="ml-2 text-gray-500">
                                → "{group.canonical}"
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showSimilarGroups && similarGroups.length === 0 && !analyzing && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Ingen lignende registreringsnummer funnet</h3>
                <p className="text-gray-600">
                  Alle registreringsnummer ser ut til å være unike eller allerede standardiserte.
                </p>
                <button
                  onClick={resetAnalysis}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Tilbake til liste
                </button>
              </div>
            </div>
          )}

          {/* Manual management section */}
          {!showSimilarGroups && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <h2 className="text-lg font-medium text-gray-900">
                    Registreringsnummer ({licensePlates.length})
                  </h2>
                  <div className="relative">
                    <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Søk etter registreringsnummer..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {selectedPlates.size > 1 && (
                    <div className="flex items-center space-x-3 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
                      <span className="text-sm font-medium text-blue-900">
                        {selectedPlates.size} registreringsnummer valgt
                      </span>
                      <div className="flex items-center space-x-2">
                        <label className="text-sm font-medium text-blue-900">
                          Slå sammen til:
                        </label>
                        <select
                          value={canonicalName}
                          onChange={(e) => setCanonicalName(e.target.value)}
                          className="rounded-md border-blue-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                        >
                          <option value="">Velg kanonisk registreringsnummer</option>
                          {Array.from(selectedPlates).map(plateName => (
                            <option key={plateName} value={plateName}>
                              {plateName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        onClick={executeMerge}
                        disabled={merging || !canonicalName || selectedPlates.size < 2}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400"
                      >
                        {merging ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Slår sammen...
                          </>
                        ) : (
                          <>
                            <Merge className="h-4 w-4 mr-2" />
                            Slå sammen
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Velg
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Registreringsnummer
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Antall registreringer
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Handlinger
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredPlates.map((plate, index) => (
                      <tr key={plate.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedPlates.has(plate.licensePlate)}
                            onChange={(e) => handlePlateSelection(plate.licensePlate, e.target.checked)}
                            disabled={editingId === plate.id}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingId === plate.id ? (
                            <input
                              type="text"
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                              onKeyPress={(e) => e.key === 'Enter' && handleSave(plate.licensePlate)}
                              autoFocus
                            />
                          ) : (
                            <div className="text-sm font-medium text-gray-900">{plate.licensePlate}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {plate.count}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {editingId === plate.id ? (
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => handleSave(plate.licensePlate)}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                              >
                                <Save className="h-3 w-3 mr-1" />
                                Lagre
                              </button>
                              <button
                                onClick={handleCancel}
                                className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                              >
                                <X className="h-3 w-3 mr-1" />
                                Avbryt
                              </button>
                            </div>
                          ) : (
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => handleEdit(plate)}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-blue-600 hover:text-blue-900"
                              >
                                <Edit2 className="h-3 w-3 mr-1" />
                                Rediger
                              </button>
                              <button
                                onClick={() => handleDeleteAll(plate.licensePlate, plate.count)}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-600 hover:text-red-900"
                              >
                                <Trash2 className="h-3 w-3 mr-1" />
                                Slett alle ({plate.count})
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {filteredPlates.length === 0 && !loading && (
                  <div className="text-center py-12">
                    <div className="text-gray-500">
                      <p className="text-lg font-medium">
                        {searchTerm ? 'Ingen registreringsnummer funnet' : 'Ingen registreringsnummer funnet'}
                      </p>
                      <p className="mt-2">
                        {searchTerm ? 'Prøv et annet søkeord.' : 'Det er ingen registreringer å vise.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Progress bar for merge operation */}
              {mergeProgress && (
                <div className="px-6 py-4 border-t border-gray-200 bg-blue-50">
                  <div className="flex justify-between text-sm text-blue-700 mb-1">
                    <span>Behandler sammenslåinger...</span>
                    <span>{mergeProgress.current} / {mergeProgress.total}</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(mergeProgress.current / mergeProgress.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}